import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loadConnectAndInitialize, StripeConnectInstance } from "@stripe/connect-js";

import { createStripeClientSecret } from "../../api/services/payment";
import { useStripeAccountId } from "../../api/queries/payment/paymentQueries";
import { useConnectStripPaymentAccount } from "../../api/queries/payment/paymentMutations";
import APP_ROUTES from "../../routers/routeConstants";
import typography from "../../theme/typography";
import { colors } from "../../theme/palette";

export const useStripeConnect = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const { stripeAccountId, isStripeAccountIdLoading, isStripeAccountIdHasError } = useStripeAccountId();

  useEffect(() => {
    if (isStripeAccountIdLoading || isStripeAccountIdHasError || !stripeAccountId) {
      return;
    }

    const initializeStripeConnect = async () => {
      setIsLoading(true);
      try {
        const instance = await loadConnectAndInitialize({
          publishableKey: process.env.REACT_APP_NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string,
          fetchClientSecret: async () => createStripeClientSecret(stripeAccountId),
          appearance: {
            overlays: "dialog",
            variables: {
              fontFamily: typography.fontFamily,
              colorPrimary: colors.Green100,
              colorText: colors.Black100,
              colorBackground: colors.White,
            },
          },
          locale: i18n.language,
        });
        setStripeConnectInstance(instance);
      } catch (initError) {
        console.error("Error initializing Stripe Connect:", initError);
        setError(initError as Error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeStripeConnect();
  }, [stripeAccountId, i18n.language, isStripeAccountIdLoading, isStripeAccountIdHasError]);

  const { mutate } = useConnectStripPaymentAccount();
  const handleOnFinishingOnBoardingSteps = () => {
    mutate(stripeAccountId);
    navigate(APP_ROUTES.PAYMENT);
  };

  return {
    stripeConnectInstance,
    isStripeConnectInstanceLoading: isLoading || isStripeAccountIdLoading,
    isStripeConnectInstanceHasError: error || isStripeAccountIdHasError,
    handleOnFinishingOnBoardingSteps,
  };
};

export default useStripeConnect;
